// src/auth.ts — front-end identity. Reads role + caps from /api/me (the single
// source of truth), NEVER from /.auth/me's userRoles (we don't use rolesSource).

import { useEffect, useState } from "react";

export type Role = "admin" | "lineadmin" | "lineviewer" | "editor" | "viewer";
export type UserStatus = "active" | "invited" | "deactivated";

export interface Caps {
  lineView: boolean;
  lineEdit: boolean;
  otherView: boolean;
  otherEdit: boolean;
  admin: boolean;
}

export interface Me {
  userId: number;
  name: string;
  email: string;
  dept: string;
  title: string | null;
  role: Role;
  status: UserStatus;
  isSsoAdmin: boolean;
  /** Per-user STN approval permission (the Operating-Manager sign-off). Gates "Approve & issue". */
  isStnApprover?: boolean;
  /** Can raise an Engineering Possession Order (any Control/lineEdit user OR the per-user flag). */
  canRaisePossession?: boolean;
  caps: Caps;
}

interface AuthMeResponse {
  clientPrincipal: { userId: string; userDetails: string } | null;
}

export function useCurrentUser() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // 1. Is there a SWA session? If not, drive the login flow.
        const authRes = await fetch("/.auth/me");
        const auth = (await authRes.json()) as AuthMeResponse;
        if (!auth.clientPrincipal) {
          const returnTo = window.location.pathname + window.location.search;
          window.location.replace(
            `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`,
          );
          return;
        }

        // 2. Get identity + role + caps from our API (not /.auth/me).
        const meRes = await fetch("/api/me");
        if (meRes.status === 401) {
          window.location.replace("/.auth/login/aad");
          return;
        }
        if (!meRes.ok) throw new Error(`/api/me returned ${meRes.status}`);
        const data = (await meRes.json()) as Me;
        if (!cancelled) setMe(data);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { me, loading, error };
}

// ── Role / caps helpers (mirror the server's bp-data.jsx cap() exactly) ──

const ROLE_LABELS: Record<Role, string> = {
  admin: "Admin",
  lineadmin: "Line Admin",
  lineviewer: "Line Viewer",
  editor: "Editor",
  viewer: "Viewer",
};

export function roleLabel(role: Role): string {
  return ROLE_LABELS[role] ?? role;
}

// One-line description per role (mirrors bp-data.jsx ROLES blurbs) — shown in the
// Users & Access role legend + the invite modal.
export const ROLE_BLURB: Record<Role, string> = {
  admin: "Everything — Line, planning tabs, and user management.",
  lineadmin: "Full edit on the Line tab (shunting, movements, possessions, turns). Views the rest.",
  lineviewer: "View-only the Line tab. No access to the planning tabs.",
  editor: "Edit the planning tabs (Snapshot / Matrix / Calendar / notes). No Line access.",
  viewer: "View-only across the planning tabs and the Line.",
};

const ROLE_AXES: Record<Role, { line: "edit" | "view" | "none"; other: "edit" | "view" | "none"; admin: boolean }> = {
  admin:      { line: "edit", other: "edit", admin: true },
  lineadmin:  { line: "edit", other: "view", admin: false },
  lineviewer: { line: "view", other: "none", admin: false },
  editor:     { line: "none", other: "edit", admin: false },
  viewer:     { line: "view", other: "view", admin: false },
};

/** Client-side caps for a role — used by the "View as role" preview (Phase 6). */
export function capsForRole(role: Role): Caps {
  const a = ROLE_AXES[role] ?? ROLE_AXES.viewer;
  return {
    lineView: a.line !== "none",
    lineEdit: a.line === "edit",
    otherView: a.other !== "none",
    otherEdit: a.other === "edit",
    admin: a.admin,
  };
}

export const ROLE_ORDER: Role[] = ["admin", "lineadmin", "lineviewer", "editor", "viewer"];
